// src/billing/disclosureCopy.js — the backend's disclosure codes and capability losses → OUR sentences.
//
// ⭐ v203: the backend no longer sends a `disclosure` sentence. Every confirmation that buys, changes or cancels is
// worded here from `disclosure_codes` (options[] rows, packs_disclosure_codes, cancel_disclosure_codes, and the
// session responses) and from a change row's `discloses[]`. Codes arrive in the order to word them
// (BE src/api/routes/billing_disclosure_codes.py).
// ⛔ No credit amounts, and no sentence about plan credits being kept or replaced: subscriptions are sold per
// period (product decision 2026-10-07). PLAN_CREDITS_RESTART is therefore KNOWN and worded as NOTHING — it must
// never reach the unknown-code fallback, or every immediate change would show the generic sentence.
// ⛔ An unknown code or loss key prints ONE generic sentence, never the raw code.
// ⭐ v204 CHARGED_TO_SAVED_CARD is worded with the PREVIEW's figures (`ctx.charge`: the formatted total and Stripe's
// card brand / last4). Only the «Passa ora» confirmation carries it, and that one is always built from a preview;
// without a total it says nothing (it is known, so it never reaches the generic fallback either).

export const DISCLOSURE_KEY = {
  PERIOD_RESTARTS_TODAY: 'disclosure.PERIOD_RESTARTS_TODAY',
  PLAN_CREDITS_RESTART: null,
  CHARGED_TO_SAVED_CARD: 'disclosure.CHARGED_TO_SAVED_CARD',
  FEWER_CREDITS_PER_PERIOD: 'disclosure.FEWER_CREDITS_PER_PERIOD',
  SCHEDULED_CHANGE_RELEASED: 'disclosure.SCHEDULED_CHANGE_RELEASED',
  CHANGE_AT_RENEWAL: 'disclosure.CHANGE_AT_RENEWAL',
  SCHEDULED_CHANGE_REPLACED: 'disclosure.SCHEDULED_CHANGE_REPLACED',
  CURRENT_PLAN_CREDITS_END: 'disclosure.CURRENT_PLAN_CREDITS_END',
  PURCHASED_CREDITS_KEPT: 'disclosure.PURCHASED_CREDITS_KEPT',
  PACK_CREDITS_NEVER_EXPIRE: 'disclosure.PACK_CREDITS_NEVER_EXPIRE',
  PACK_COVERS_NEGATIVE_BALANCE: 'disclosure.PACK_COVERS_NEGATIVE_BALANCE',
};

/** `discloses[]` keys (capabilities the target plan has LESS of). */
export const LOSS_KEY = {
  max_users: 'disclosure.loses.max_users',
  max_tenants: 'disclosure.loses.max_tenants',
  granted_features: 'disclosure.loses.granted_features',
  enforces_limit: 'disclosure.loses.enforces_limit',
  allows_engine_reruns: 'disclosure.loses.allows_engine_reruns',
};

/** The codes whose sentence names the plan / period; the others take no variables. */
const INTERPOLATED = new Set(['PERIOD_RESTARTS_TODAY', 'CHANGE_AT_RENEWAL']);

const UNKNOWN = 'disclosure.unknown';
const has = (table, key) => Object.prototype.hasOwnProperty.call(table, key);

/** Stripe's card `brand` ids → the names printed on the card. An unknown brand is not guessed at. */
const CARD_BRANDS = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club',
  jcb: 'JCB', unionpay: 'UnionPay', cartes_bancaires: 'Cartes Bancaires',
};
export function cardBrandName(brand) {
  return typeof brand === 'string' && has(CARD_BRANDS, brand) ? CARD_BRANDS[brand] : null;
}

/** The CHARGED_TO_SAVED_CARD sentence, or null without a total. */
function chargeLine(t, charge) {
  if (!charge?.total) return null;
  const brand = cardBrandName(charge.brand);
  return brand && charge.last4
    ? t('disclosure.CHARGED_TO_SAVED_CARD', { total: charge.total, brand, last4: charge.last4 })
    : t('disclosure.CHARGED_TO_SAVED_CARD_generic', { total: charge.total });
}

/** The period word for PERIOD_RESTARTS_TODAY, from the TARGET row's `billing_interval` (null → «periodo»). */
function periodWord(t, interval) {
  return t(`disclosure.period.${interval === 'month' || interval === 'year' ? interval : 'other'}`);
}

/**
 * @param {Function} t        the `billing` translator
 * @param {string[]} codes    disclosure codes, in the backend's order
 * @param {{plan?: string, interval?: string|null, charge?: {total: string, brand?: string|null, last4?: string|null}}} [ctx]
 *   the target plan's display name and billing interval; `charge` = the preview's formatted total and card
 * @returns {string[]} one sentence per worded code (+ one generic sentence if any code is unknown)
 */
export function disclosureLines(t, codes, { plan = '', interval = null, charge = null } = {}) {
  if (!Array.isArray(codes)) return [];
  const vars = { plan, period: periodWord(t, interval) };
  const lines = [];
  let unknown = false;
  codes.forEach((code) => {
    if (!has(DISCLOSURE_KEY, code)) { unknown = true; return; }
    if (!DISCLOSURE_KEY[code]) return;
    if (code === 'CHARGED_TO_SAVED_CARD') {
      const line = chargeLine(t, charge);
      if (line) lines.push(line);
      return;
    }
    lines.push(INTERPOLATED.has(code) ? t(DISCLOSURE_KEY[code], vars) : t(DISCLOSURE_KEY[code]));
  });
  if (unknown) lines.push(t(UNKNOWN));
  return lines;
}

/** One sentence per capability the new plan has less of; an unknown key → the generic sentence once. */
export function lossLines(t, discloses) {
  if (!Array.isArray(discloses)) return [];
  const lines = [];
  let unknown = false;
  discloses.forEach((key) => {
    if (has(LOSS_KEY, key)) lines.push(t(LOSS_KEY[key]));
    else unknown = true;
  });
  if (unknown) lines.push(t(UNKNOWN));
  return lines;
}
